import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);
const CACHE_KEY = "danashakaa.user";

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (user) => {
  try {
    if (user && typeof user === "object") {
      localStorage.setItem(CACHE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch {
    // ignore
  }
};

export const AuthProvider = ({ children }) => {
  // Hydrate immediately from localStorage so the UI never blocks on /auth/me.
  // Values: null = checking (no cache), object = authenticated, false = unauthenticated.
  const cached = typeof window !== "undefined" ? readCache() : null;
  const [user, setUserState] = useState(cached || null);
  const [loading, setLoading] = useState(!cached);

  const setUser = useCallback((u) => {
    setUserState(u);
    writeCache(u && typeof u === "object" ? u : null);
  }, []);

  const fetchMe = useCallback(async () => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    // Always revalidate in the background, even if we hydrated from cache.
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(false);
  };

  const refresh = fetchMe;

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
